/**
 * The price bound a perp MARKET order (or market close) carries on-chain.
 *
 * Until this existed every market order sent `acceptablePrice = MaxUint256` (a
 * buy) or `0` (a sell) — "fill me at any price". The bound is now the trader's
 * max-slippage setting applied to the touch, read from the chain at submit time.
 *
 * Why the touch and not a VWAP or the mark (probed live on Arc, see
 * docs/TRADING-INPUT-STANDARD.md §5a): the contract checks `acceptablePrice`
 * against EACH fill's price, inclusive, and a market order is all-or-nothing.
 * A bound derived from the average fill price rejects orders it should accept.
 *
 * Why read it here rather than take it from the form: the websocket book the
 * form renders can be seconds old, and a bound from a stale touch either
 * refuses a good order or loosens the protection. A view call at submit is the
 * freshest price there is. If it cannot be read, nothing is signed — an
 * unbounded order is exactly what this replaced.
 */
import { BigNumber, ethers } from "ethers";
import { orderRejected } from "./orderTransactionError";
import type { ChainConfig } from "../../../config/chains";
import { perpAcceptablePrice } from "../../../utils/amount/slippage";
import { readProvider } from "../../../utils/wallet/activeSigner";

/** OrderHandler's top-of-book view. Side 0 = bids, 1 = asks (probed on Arc, Monad, DogeOS). */
export const PERP_BEST_PRICE_ABI = [
  "function getBestPrice(address _market, uint8 _side) view returns (uint128 price, uint256 volume)",
];

const BID_SIDE = 0;
const ASK_SIDE = 1;

export const TOUCH_UNREADABLE_MESSAGE =
  "Couldn't read the current order-book price, so the order was not sent. Try again in a moment.";

export const emptySideMessage = (isBuy: boolean): string =>
  isBuy
    ? "There are no sell orders on the book right now, so a market buy can't fill. Nothing was sent."
    : "There are no buy orders on the book right now, so a market sell can't fill. Nothing was sent.";

/**
 * The price a market order meets first: the best ask for a buy, the best bid
 * for a sell. Throws a sentence the trader can act on when it is unknowable.
 */
export const readPerpTouch = async (
  config: ChainConfig,
  market: string,
  isBuy: boolean
): Promise<BigNumber> => {
  const orderHandler = config.contracts.perpetualDex?.orderHandler;
  if (!orderHandler || orderHandler === ethers.constants.AddressZero) {
    throw orderRejected(TOUCH_UNREADABLE_MESSAGE);
  }
  let price: BigNumber;
  try {
    const book = new ethers.Contract(orderHandler, PERP_BEST_PRICE_ABI, readProvider(config));
    const best = await book.getBestPrice(market, isBuy ? ASK_SIDE : BID_SIDE);
    price = BigNumber.from(best?.price ?? best?.[0] ?? 0);
  } catch {
    throw orderRejected(TOUCH_UNREADABLE_MESSAGE);
  }
  if (price.lte(0)) throw orderRejected(emptySideMessage(isBuy));
  return price;
};

/**
 * `acceptablePrice` for a market order: the touch moved against the trader by
 * their tolerance, snapped strictly onto the price grid. `isBuy` is the side
 * of the ORDER — closing a long is a sell.
 */
export const marketAcceptablePrice = async ({
  config,
  market,
  isBuy,
  slippageBps,
  priceStep,
}: {
  config: ChainConfig;
  market: string;
  isBuy: boolean;
  slippageBps: number;
  priceStep?: BigNumber;
}): Promise<BigNumber> => {
  const touch = await readPerpTouch(config, market, isBuy);
  const bound = perpAcceptablePrice({
    isLong: isBuy,
    refPriceRaw: touch.toBigInt(),
    bps: slippageBps,
    priceStepRaw: priceStep && priceStep.gt(0) ? priceStep.toBigInt() : undefined,
  });
  return BigNumber.from(bound.toString());
};
